import type { ComponentProps } from "react";

import { CircularProgress } from "@/components/ui/circular-progress";
import { ItemImageBox } from "@/components/ui/item-image-box";
import { useItemImageFailure } from "@/hooks/use-item-image-failure";
import { itemImageSources, type CatalogItemImageInput } from "@/lib/catalog-item-image";
import { cn } from "@/lib/utils";

// The catalog card and list row's variant tile strip — the donor's shared
// `ItemVariantsTiles` (`item-variants-tiles.tsx`), which both views render with
// the same four arguments.
//
// **The donor has three variant tile strips and they are not interchangeable.**
// This is the catalog one. The spec editor's item search card
// (`ItemSearchIsland/components/ItemCard/VariantTiles.tsx`) and the Add Variant
// dialog (`AddVariantIsland/components/item-details.tsx:249`) each have their
// own: 28px tiles, `MAX_VARIANT_TILES = 4` with a `+N variant(s)` overflow
// label, the current tile moved to the front, a black-vs-`#C4C4C4` border, a
// CSS background rather than an `<img>`, and no button semantics. This one caps
// nothing, reorders nothing, and gives every tile a real button. Reaching for
// the wrong one is the easy mistake here, so: `xsmall`, uncapped, in group
// order. (The Atlas port of the dialog's strip does now carry button semantics,
// on an owner ruling — #254's D2 — so that last difference is the donor's
// rather than this app's; `add-variant-preview-card.tsx` records it.)
//
// Here in `components/ui/` because the donor's file is shared, and a shared
// donor component ports as a shared Atlas component at first encounter
// (`docs/agents/migration-guidelines.md`). It first shipped under
// `components/catalog/` on the argument that both its callers are this feature's
// and that reading a catalog item's `heroImage` shape is knowledge a `ui/`
// component should not hold; the owner ruled against that (#254's D7), and the
// rule's default is the reason — placement follows the donor's sharing, not the
// current caller list, because the move only gets more expensive per caller
// added. The `heroImage` coupling is real but narrow: it is the same
// `CatalogItemImageInput` that `@/components/ui/item-image-box` beside it
// already takes.

/** One member of the group, as the strip reads it — the list response's
 * `variantGroups[].members[]` entry (`variantHeroWire`). */
export interface CatalogVariantTile {
  itemId: number;
  /** `null` when that sibling has no image: the donor still draws its tile as a
   * placeholder rather than filtering the member out. */
  heroImage?: CatalogItemImageInput | null;
}

interface ItemVariantTilesProps {
  members: readonly CatalogVariantTile[];
  /** The sibling currently previewed — already resolved through the stale-preview
   * guard by the caller, so this is simply "which tile is pressed". */
  selectedVariantId: number;
  /** The sibling whose details are in flight, if any. */
  loadingVariantId?: number;
  onSelect: (itemId: number) => void;
  className?: string;
}

export function ItemVariantTiles({
  members,
  selectedVariantId,
  loadingVariantId,
  onSelect,
  className,
}: ItemVariantTilesProps) {
  // `variants.length === 0` is the donor's own gate (`item-variants-tiles.tsx:23`)
  // — an ungrouped item draws no strip at all, which is most of the catalog.
  if (members.length === 0) return null;

  return (
    <div
      data-testid="item-variants-tiles"
      // `Stack direction='row' spacing={1} wrap`. Modsy's `Stack` passes
      // `useFlexGap={true}` (`packages/react-ui/src/stack/stack.tsx:73`), so that
      // 8px is a real `gap` on **both** axes — a wrapped second row sits 8px
      // below the first rather than flush against it, which margin-based MUI
      // spacing would have got wrong.
      className={cn("flex flex-wrap gap-2", className)}
    >
      {members.map((tile) => (
        <VariantTile
          key={tile.itemId}
          tile={tile}
          isCurrent={tile.itemId === selectedVariantId}
          isLoading={tile.itemId === loadingVariantId}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

/**
 * One tile — its own component because the failure verdict is **per tile**.
 *
 * `useItemImageFailure` is a hook, so a strip that asked for one verdict per
 * member inside `.map()` would be calling hooks in a loop whose length is the
 * data's. One component per tile is what makes the verdict per slot, which is
 * the property the hook exists to hold (`use-item-image-failure.ts`).
 */
function VariantTile({
  tile,
  isCurrent,
  isLoading,
  onSelect,
}: {
  tile: CatalogVariantTile;
  isCurrent: boolean;
  isLoading: boolean;
  onSelect: (itemId: number) => void;
}) {
  const sources = itemImageSources(tile.heroImage);
  // Keyed on the URLs, so a tile pointed at different bytes retries rather than
  // staying fallen back, and a verdict cannot outlive the fetch it described.
  const [failed, noteFailed] = useItemImageFailure(sources);
  const picture = tile.heroImage && !failed ? sources : null;

  // Shared by both arms below, because the box is the one thing that must *not*
  // change when the picture fails: same size, same hairline, same border
  // colour, only the media inside it differs. Held in one place so the two arms
  // cannot drift apart.
  //
  // The alt stays the tile's own in both: modsy's failed tile keeps it too,
  // since over there the placeholder *is* the picture.
  const boxProps = {
    size: "xsmall",
    alt: `Variant ${tile.itemId} image`,
    bordered: isCurrent ? ("primary" as const) : true,
  } satisfies Partial<ComponentProps<typeof ItemImageBox>>;

  return (
    <button
      type="button"
      data-testid={`item-variant-tile-${tile.itemId}`}
      // The tile's picture says nothing a sighted user needs and nothing a
      // screen-reader user can act on, so the *button* carries the name.
      // The donor's own label, id and all — the strip has no item names to
      // work with: a group member arrives as `{ itemId, heroImage }`.
      aria-label={`Show variant ${tile.itemId}`}
      aria-pressed={isCurrent}
      disabled={isLoading}
      onClick={() => onSelect(tile.itemId)}
      // `lineHeight: 0` matters: without it the inline image box sits on a
      // text baseline and the button is a few pixels taller than its tile.
      //
      // No hover treatment and **no ripple**: the donor's tile is a
      // `Box component='button'`, not a `ButtonBase`, so it is the one
      // clickable thing on these screens that MUI does not ink. The house
      // rule runs the other way, which is exactly why it is worth saying.
      className={cn(
        "relative cursor-pointer rounded-[4px] leading-[0] disabled:cursor-default",
        "focus-visible:outline-lennar-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2",
      )}
    >
      {failed ? (
        // **The picture failed, so draw the donor's generic "no picture"
        // graphic** — the same box, hairline and border colour, so only
        // the media inside the tile changes.
        //
        // Modsy reaches the same screen by a different road: its tile has
        // no `onError` and needs none, because the image host answers a
        // missing object with a 403 whose *body is a decodable PNG*, so
        // the `<img>` loads a placeholder rather than erroring (measured
        // on the running stack, #496). Atlas serves its own objects and a
        // missing one really does fail, so the placeholder is drawn here
        // instead of arriving over the wire.
        //
        // The donor's own in-repo asset — the one its *other* variant strip
        // already substitutes
        // (`AddVariantIsland/components/item-details.tsx:322`) — rather
        // than the CDN's error artwork, which is dev-distribution
        // configuration this app cannot verify for production.
        <ItemImageBox placeholder {...boxProps} />
      ) : (
        <ItemImageBox
          src={picture?.src}
          sources={picture?.sources}
          onError={noteFailed}
          {...boxProps}
        />
      )}
      {isLoading && (
        // `CircularProgress size={16}` at `top/left: 50%` with -8px
        // margins — the donor's tile exactly
        // (`item-variants-tiles.tsx:92-101`).
        //
        // **In `primary.main`**, which is the component's default: the
        // donor's tile passes no `color`
        // (`CircularProgress.js:122,206`), so it spins Lennar blue. The
        // dialog button's spinner is the opposite case and asks to
        // inherit, as the donor's `Button` does
        // (`react-ui/src/button/button.tsx:23`).
        <CircularProgress
          size={16}
          data-slot="variant-tile-spinner"
          className="absolute top-1/2 left-1/2 -mt-2 -ml-2"
        />
      )}
    </button>
  );
}
